/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { TouchableOpacity, View, StyleSheet, Dimensions } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import SociollaText from '@components/sociolla/SociollaText';
import RenderIf from '@components/global/RenderIf';
import TitleSubSection from '@components/global/TitleSubSection';
import StatusBadge from '@components/global/StatusBadge';
import Image from '@components/global/Image';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import useApi from '@/hooks/useApi';
import { MsOrder, updateProductListingItems } from '@api/ms-order';
import { getAttibutes } from '@global/helpers';
import { showPopup } from '@/store/modal';
import { parseDate, escapeRegexChars } from '@global/helpers';
import { selectParams, getCurrentParams } from '@/store/params';
import store from '@/store';
import {
  REQUEST_PRODUCT_LISTING_ITEMS,
  REQUEST_PRODUCT_LISTING_SUMMARY_ITEMS,
  REQUEST_PRODUCT_LISTING_SUMMARY_LOG_ITEMS,
} from '@/constants/table-name';

import TableProductListingItem from './TableProductListingItem';

const propTypes = {
  id: PropTypes.string,
  detailData: PropTypes.object,
  mobileTab: PropTypes.number,
  navigation: PropTypes.object,
};

const TABS = [
  { key: 1, name: 'Product Listings' },
  { key: 2, name: 'Product Summary & Logs' },
];

const MOBILE_TABS = [
  { key: 1, name: 'Product Summary' },
  { key: 2, name: 'Logs' },
];

const HEADER_SUMMARY = [
  {
    head: 'Image',
    body: 'image',
    type: 'image',
  },
  {
    head: 'Product Name',
    body: 'ref_name',
  },
  {
    head: 'Attribute',
    body: 'variant',
  },
  {
    head: 'Category',
    body: 'category',
  },
  {
    head: 'Qty',
    body: 'quantity',
  },
];

const HEADER_SUMMARY_LOG_CUSTOMER = [
  {
    head: 'Request Product Number',
    body: 'reference',
    type: 'slot',
  },
  {
    head: 'Address Name',
    body: 'address_name',
  },
  {
    head: 'Image',
    body: 'image',
    type: 'image',
  },
  {
    head: 'Product Name',
    body: 'ref_name',
  },
  {
    head: 'Category',
    body: 'category',
  },
  {
    head: 'Qty',
    body: 'quantity',
  },
  {
    head: 'Status',
    body: 'status',
    type: 'slot',
  },
  {
    head: 'Last Updated',
    body: 'updated_at',
    type: 'slot',
  },
];

const HEADER_SUMMARY_LOG_SUBDISTRIBUTOR = [
  {
    head: 'Request Product Number',
    body: 'reference',
    type: 'slot',
  },
  {
    head: 'Outlet Name',
    body: 'outlet_name',
  },
  {
    head: 'Outlet Code',
    body: 'outlet_code',
  },
  {
    head: 'Image',
    body: 'image',
    type: 'image',
  },
  {
    head: 'Product Name',
    body: 'ref_name',
  },
  {
    head: 'Category',
    body: 'category',
  },
  {
    head: 'Qty',
    body: 'quantity',
  },
  {
    head: 'Status',
    body: 'status',
    type: 'slot',
  },
  {
    head: 'Last Updated',
    body: 'updated_at',
    type: 'slot',
  },
];

const defaultPhoto = require('@/assets/img/default/user-default.png');
const dimension = Dimensions.get('screen');

const ProductListingTab = (props) => {
  const { id, detailData, mobileTab, navigation } = props;
  const dispatch = useDispatch();

  const didFetchRef = useRef(false);

  const [activeTab, setActiveTab] = useState(1);
  const [activeMobileTab, setActiveMobileTab] = useState(1);

  const [products, setProducts] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const paramsState = useSelector(selectParams(REQUEST_PRODUCT_LISTING_ITEMS));

  const [productsSum, setProductsSum] = useState([]);
  const [productCountSum, setProductCountSum] = useState(0);
  const [loadingMoreSum, setLoadingMoreSum] = useState(false);
  const [searchValueSum, setSearchValueSum] = useState('');
  const paramsStateSum = useSelector(
    selectParams(REQUEST_PRODUCT_LISTING_SUMMARY_ITEMS),
  );

  const [productsSumLog, setProductsSumLog] = useState([]);
  const [productCountSumLog, setProductCountSumLog] = useState(0);
  const [loadingMoreSumLog, setLoadingMoreSumLog] = useState(false);
  const paramsStateSumLog = useSelector(
    selectParams(REQUEST_PRODUCT_LISTING_SUMMARY_LOG_ITEMS),
  );

  const getProductListingItems = useApi(MsOrder.getProductListingItems);
  const getProductListingItemsCount = useApi(
    MsOrder.getProductListingItemsCount,
  );

  const getProductListingSummary = useApi(MsOrder.getProductListingSummary);
  const getProductListingSummaryCount = useApi(
    MsOrder.getProductListingItemsCount,
  );

  const getProductListingSummaryLog = useApi(
    MsOrder.getProductListingSummaryLog,
  );
  const getProductListingSummaryLogCount = useApi(
    MsOrder.getProductListingSummaryLogCount,
  );

  const getProducts = async (params) => {
    let totalCount = productCount;
    if (params.skip == 0) {
      const countResponse = await getProductListingItemsCount.request(id, {
        params,
      });
      if (countResponse && countResponse.data && countResponse.data.data) {
        totalCount = countResponse.data.data;
      } else {
        totalCount = 0;
      }
    }
    setProductCount(totalCount);

    const response = await getProductListingItems.request(id, {
      params: params,
    });
    const result = response?.data?.data || [];

    const finalResult = result.map((p) => ({
      ...p,
      ref_name: `[${p.combination.reference}] ${p.name}`,
      variant: getAttibutes(p.combination.attributes),
      image: p.combination.image,
      category: p.category.name,
    }));

    if (params.skip > 0 && !isWeb) {
      setProducts((prev) => [...prev, ...finalResult]);
    } else {
      setProducts(finalResult);
    }
  };

  const getProductsSummary = async (params) => {
    let totalCount = productCountSum;
    if (params.skip == 0) {
      const countResponse = await getProductListingSummaryCount.request(id, {
        params,
      });
      if (countResponse && countResponse.data && countResponse.data.data) {
        totalCount = countResponse.data.data;
      } else {
        totalCount = 0;
      }
    }
    setProductCountSum(totalCount);

    const response = await getProductListingSummary.request(id, {
      params: params,
    });
    const result = response?.data?.data || [];

    const finalResult = result.map((p) => ({
      ...p,
      ref_name: `[${p.combination.reference}] ${p.name}`,
      variant: getAttibutes(p.combination.attributes),
      image: p.combination.image,
      category: p.category.name,
    }));

    if (params.skip > 0 && !isWeb) {
      setProductsSum((prev) => [...prev, ...finalResult]);
    } else {
      setProductsSum(finalResult);
    }
  };

  const getProductsSummaryLog = async (params) => {
    let totalCount = productCountSumLog;
    if (params.skip == 0) {
      const countResponse = await getProductListingSummaryLogCount.request(id, {
        params,
      });
      if (countResponse && countResponse.data && countResponse.data.data) {
        totalCount = countResponse.data.data;
      } else {
        totalCount = 0;
      }
    }
    setProductCountSumLog(totalCount);

    const response = await getProductListingSummaryLog.request(id, {
      params: params,
    });
    const result = response?.data?.data || [];

    const finalResult = result.map((p) => {
      p.ref_name = `[${p.combination.reference}] ${p.name}`;
      p.variant = getAttibutes(p.combination.attributes);
      p.image = p.combination.image;
      p.category = p.category.name;
      p.address_name = p.address.name;

      if (p.outlet) {
        p.outlet_name = p.outlet.name;
        p.outlet_code = p.outlet.code;
      }

      return p;
    });

    if (params.skip > 0 && !isWeb) {
      setProductsSumLog((prev) => [...prev, ...finalResult]);
    } else {
      setProductsSumLog(finalResult);
    }
  };

  const onSearchTableSummary = async (val, isMobile) => {
    if (isMobile) {
      setLoadingMoreSum(true);
    }
    if (val) {
      setSearchValueSum(val);
    }
    const params = getCurrentParams(
      store.getState(),
      REQUEST_PRODUCT_LISTING_SUMMARY_ITEMS,
    );

    const finalParams = { ...params };
    if (finalParams?.filter?.keyword) {
      const keyword =
        finalParams.filter.keyword?.$regex ?? finalParams.filter.keyword;
      finalParams.filter['$or'] = [
        {
          name: { $regex: escapeRegexChars(keyword), $options: 'i' },
        },
        {
          'combination.reference': { $regex: keyword, $options: 'i' },
        },
        {
          'combination.ean_no': { $regex: keyword, $options: 'i' },
        },
        {
          'brand.name': { $regex: keyword, $options: 'i' },
        },
      ];
      delete finalParams.filter.keyword;
    }
    await getProductsSummary({ ...finalParams });
    if (isMobile) {
      setLoadingMoreSum(false);
    }
  };

  const onSearchTableSummaryLog = async (val, isMobile) => {
    if (isMobile) {
      setLoadingMoreSumLog(true);
    }
    const params = getCurrentParams(
      store.getState(),
      REQUEST_PRODUCT_LISTING_SUMMARY_LOG_ITEMS,
    );

    const finalParams = { ...params };
    await getProductsSummaryLog({ ...finalParams });
    if (isMobile) {
      setLoadingMoreSumLog(false);
    }
  };

  const resetData = () => {
    setActiveTab(1);
    setActiveMobileTab(1);
    setProducts([]);
    setProductCount(0);
    setProductsSum([]);
    setProductCountSum(0);
    setProductsSumLog([]);
    setProductCountSumLog(0);
  };

  const handleEditProduct = async (prod) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await updateProductListingItems(id, prod.id, prod.combination.id, {
        name: prod.name,
        is_active: !prod.is_active,
      });
      await getProducts(paramsState);
      Toast.show({
        text1: 'Successfully update product',
        type: 'success',
      });
    } catch (error) {
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const handleGoToRpt = (data) => {
    const params = {
      id: data.request_product.id,
      reference_code: data.request_product.reference_code,
      prev_page: 'product-listing',
      prev_id: id,
    };
    navigation.navigate('RequestProductNavigator', {
      screen: 'RequestProductDetailScreen',
      params: params,
    });
  };

  const productSummaryTab = useMemo(() => {
    if (isWeb) {
      return { isShow: true, tabs: TABS };
    }
    if (!isWeb && mobileTab === 2) {
      return { isShow: true, tabs: MOBILE_TABS };
    }
    return { isShow: false, tabs: [] };
  }, [mobileTab, isWeb]);

  useFocusEffect(
    useCallback(() => {
      if (!didFetchRef.current) {
        didFetchRef.current = true;
        resetData();
        if (id) {
          getProducts(paramsState);
          getProductsSummary(paramsStateSum);
          getProductsSummaryLog(paramsStateSumLog);
        }
      }

      return () => {
        didFetchRef.current = false;
        resetData();
      };
    }, [id]),
  );

  const renderItemSummary = ({ data, index }) => (
    <TouchableOpacity
      key={'list-product-' + index}
      style={[
        styles.tableRowMobile,
        {
          justifyContent: 'space-between',
        },
      ]}>
      <View style={{ marginRight: 10, alignSelf: 'flex-start' }}>
        <Image
          source={{ uri: `${data.image ? data.image : defaultPhoto}` }}
          style={{ width: 36, height: 36 }}
          width={36}
          height={36}
          defaultSource={defaultPhoto}
        />
      </View>
      <View style={{ flex: 1, paddingRight: 10 }}>
        <SociollaText
          type="medium"
          style={{ fontSize: 12, color: Colors.black1, paddingBottom: 8 }}>
          {data.brand.name}
        </SociollaText>
        <SociollaText
          style={{ fontSize: 12, color: Colors.black5, paddingBottom: 8 }}>
          {data.name}
        </SociollaText>
        <SociollaText
          type="medium"
          style={{ fontSize: 12, color: Colors.black1, paddingBottom: 8 }}>
          {data.combination.reference}
        </SociollaText>
        <SociollaText
          style={{ fontSize: 12, color: Colors.black5, paddingBottom: 8 }}>
          {data.variant}
        </SociollaText>
        <SociollaText style={{ fontSize: 10, color: Colors.black5 }}>
          Qty: {data.quantity} pcs
        </SociollaText>
      </View>
    </TouchableOpacity>
  );

  const renderItemSummaryLog = ({ data, index }) => (
    <TouchableOpacity
      key={'list-product-' + index}
      style={styles.tableRowMobile}>
      <View style={{ width: '65%' }}>
        <View>
          <SociollaText type="medium">Last Updated At</SociollaText>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Image
              style={{
                width: 23,
                height: 16,
                marginRight: 5,
              }}
              width={23}
              height={16}
              svg={true}
              source={require('@assets/img/icons/menu/calendar.svg')}
            />
            <SociollaText style={{ fontSize: 11, color: Colors.black5 }}>
              {parseDate(data.updated_at, 'd MMMM yyyy | HH:mm:ss')}
            </SociollaText>
          </View>
        </View>
        <View style={{ flexDirection: 'row', marginTop: 10 }}>
          <View style={styles.imageSummaryLog}>
            <Image
              source={{ uri: `${data.image ? data.image : defaultPhoto}` }}
              style={{ width: 36, height: 36 }}
              width={36}
              height={36}
              defaultSource={defaultPhoto}
            />
          </View>
          <View style={{ flex: 1, paddingRight: 10 }}>
            <SociollaText
              type="medium"
              style={{ fontSize: 12, color: Colors.black1, paddingBottom: 8 }}>
              {data.brand.name}
            </SociollaText>
            <SociollaText
              style={{ fontSize: 12, color: Colors.black5, paddingBottom: 8 }}>
              {data.name}
            </SociollaText>
            <SociollaText
              type="medium"
              style={{ fontSize: 12, color: Colors.black1, paddingBottom: 8 }}>
              {data.combination.reference}
            </SociollaText>
            <SociollaText
              style={{ fontSize: 12, color: Colors.black5, paddingBottom: 8 }}>
              {data.variant}
            </SociollaText>
            <SociollaText style={{ fontSize: 10, color: Colors.black5 }}>
              Qty: {data.quantity} pcs
            </SociollaText>
          </View>
        </View>
      </View>
      <View style={{ width: '30%' }}>
        <StatusBadge status={data.status} />
        <View>
          <RenderIf condition={detailData?.customer?.is_subdistributor}>
            <SociollaText style={{ fontSize: 14 }} type="medium">
              Outlet:
            </SociollaText>
            <SociollaText type="medium">{data.outlet_name}</SociollaText>
            <SociollaText>{data.outlet_code}</SociollaText>
          </RenderIf>
          <RenderIf condition={!detailData?.customer?.is_subdistributor}>
            <SociollaText style={{ fontSize: 14 }} type="medium">
              Address
            </SociollaText>
            <SociollaText>{data.address_name}</SociollaText>
          </RenderIf>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.sectionWrapper}>
      {/* Start Tab Container */}
      <RenderIf condition={productSummaryTab.isShow}>
        <View
          style={[
            styles.rowContainer,
            styles.rowBorderBottom,
            {
              justifyContent: isWeb ? 'flex-start' : 'space-around',
            },
          ]}>
          {productSummaryTab.tabs.map((item, idx) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={[
                styles.tabItem,
                (isWeb ? activeTab : activeMobileTab) == item.key
                  ? styles.tabItemActive
                  : {},
                isWeb ? { width: 200 } : { width: '50%' },
              ]}
              key={item.key}
              onPress={() => {
                if (isWeb) {
                  setActiveTab(item.key);
                } else {
                  setActiveMobileTab(item.key);
                }
              }}>
              <SociollaText
                style={[
                  styles.tabText,
                  (isWeb ? activeTab : activeMobileTab) == item.key
                    ? styles.tabTextActive
                    : {},
                  { alignSelf: 'center' },
                ]}>
                {item.name || ''}
              </SociollaText>
            </TouchableOpacity>
          ))}
        </View>
      </RenderIf>
      {/* End Tab Container */}

      {/* Start Product Listing */}
      <RenderIf
        condition={(isWeb && activeTab == 1) || (!isWeb && mobileTab === 1)}>
        <View style={{ marginTop: isWeb ? 20 : 0, height: '100%' }}>
          <TableProductListingItem
            list={products}
            getProducts={getProducts}
            productCount={productCount}
            loading={getProductListingItems.loading && products.length == 0}
            isEditMode={false}
            editProduct={handleEditProduct}
          />
        </View>
      </RenderIf>
      {/* End Product Listing */}

      {/* Start Product Summary & Logs */}
      <RenderIf
        condition={(isWeb && activeTab == 2) || (!isWeb && mobileTab === 2)}>
        <RenderIf condition={isWeb ? true : activeMobileTab == 1}>
          <View style={{ marginTop: isWeb ? 20 : 0 }}>
            <RenderIf condition={isWeb}>
              <TitleSubSection
                title={'PRODUCT SUMMARY'}
                style={{ marginBottom: 10 }}
              />
            </RenderIf>
            <View>
              <RenderIf condition={isWeb}>
                <Table
                  name={REQUEST_PRODUCT_LISTING_SUMMARY_ITEMS}
                  filterName={REQUEST_PRODUCT_LISTING_SUMMARY_ITEMS}
                  structures={HEADER_SUMMARY}
                  loading={
                    getProductListingSummary.loading && productsSum.length == 0
                  }
                  data={productsSum}
                  searchKey="keyword"
                  searchPlaceholder="Search by Name, Reference, EAN"
                  searchValue={searchValueSum}
                  hasNoHide={true}
                  hasCallback={false}
                  totalData={productCountSum}
                  skip={paramsStateSum.skip}
                  limit={paramsStateSum.limit}
                  onSearch={(val) => onSearchTableSummary(val)}
                  showFilter={false}
                  disablePointer
                />
              </RenderIf>
              <RenderIf condition={!isWeb}>
                <View style={{ height: '100%' }}>
                  <TableMobile
                    useLegendList
                    name={REQUEST_PRODUCT_LISTING_SUMMARY_ITEMS}
                    filterName={REQUEST_PRODUCT_LISTING_SUMMARY_ITEMS}
                    searchKey="keyword"
                    placeholder="Search by Name, Reference, EAN"
                    searchValue={searchValueSum}
                    loading={
                      getProductListingSummary.loading &&
                      productsSum.length == 0
                    }
                    onSearch={(val) => onSearchTableSummary(val, true)}
                    skip={paramsStateSum.skip}
                    limit={paramsStateSum.limit}
                    tableData={productsSum}
                    totalData={productCountSum}
                    loadingPagination={loadingMoreSum}
                    withLoadMore
                    withFetch
                    noResetOnRefresh
                    onLoadMore={() =>
                      onSearchTableSummary(searchValueSum, true)
                    }
                    showSearch={true}
                    renderItem={renderItemSummary}
                    tableHeight={dimension.height - 150}
                  />
                </View>
              </RenderIf>
            </View>
          </View>
        </RenderIf>

        <RenderIf condition={isWeb ? true : activeMobileTab == 2}>
          <View style={{ marginTop: isWeb ? 20 : 0 }}>
            <RenderIf condition={isWeb}>
              <TitleSubSection title={'LOGS'} style={{ marginBottom: 10 }} />
            </RenderIf>
            <View>
              <RenderIf condition={isWeb}>
                <Table
                  name={REQUEST_PRODUCT_LISTING_SUMMARY_LOG_ITEMS}
                  filterName={REQUEST_PRODUCT_LISTING_SUMMARY_LOG_ITEMS}
                  structures={
                    detailData?.customer?.is_subdistributor
                      ? HEADER_SUMMARY_LOG_SUBDISTRIBUTOR
                      : HEADER_SUMMARY_LOG_CUSTOMER
                  }
                  loading={
                    getProductListingSummaryLog.loading &&
                    productsSumLog.length == 0
                  }
                  data={productsSumLog}
                  hasNoHide={true}
                  hasCallback={false}
                  totalData={productCountSumLog}
                  skip={paramsStateSumLog.skip}
                  limit={paramsStateSumLog.limit}
                  onSearch={(val) => onSearchTableSummaryLog(val)}
                  showFilter={false}
                  showSearch={false}
                  disablePointer
                  slot={{
                    updated_at: ({ data, index }) => (
                      <SociollaText
                        key={`log-date-${index}`}
                        style={{ fontSize: 11 }}>
                        {parseDate(data.updated_at)}
                      </SociollaText>
                    ),
                    status: ({ data, index }) => (
                      <StatusBadge status={data.status} />
                    ),
                    reference: ({ data, index }) => (
                      <TouchableOpacity
                        key={`log-reference-${index}`}
                        onPress={() => handleGoToRpt(data)}>
                        <SociollaText
                          style={{ fontSize: 11, color: Colors.blue0 }}>
                          {data.request_product?.reference_code || ''}
                        </SociollaText>
                      </TouchableOpacity>
                    ),
                  }}
                />
              </RenderIf>
              <RenderIf condition={!isWeb}>
                <View style={{ height: '100%' }}>
                  <TableMobile
                    useLegendList
                    name={REQUEST_PRODUCT_LISTING_SUMMARY_LOG_ITEMS}
                    filterName={REQUEST_PRODUCT_LISTING_SUMMARY_LOG_ITEMS}
                    hideSearch={true}
                    loading={
                      getProductListingSummaryLog.loading &&
                      productsSumLog.length == 0
                    }
                    onSearch={(val) => onSearchTableSummaryLog(val, true)}
                    skip={paramsStateSumLog.skip}
                    limit={paramsStateSumLog.limit}
                    tableData={productsSumLog}
                    totalData={productCountSumLog}
                    loadingPagination={loadingMoreSumLog}
                    withLoadMore
                    withFetch
                    noResetOnRefresh
                    onLoadMore={() => onSearchTableSummaryLog(null, true)}
                    showSearch={false}
                    renderItem={renderItemSummaryLog}
                    tableHeight={dimension.height - 120}
                  />
                </View>
              </RenderIf>
            </View>
          </View>
        </RenderIf>
      </RenderIf>
      {/* End Product Summary & Logs */}
    </View>
  );
};

const styles = StyleSheet.create({
  sectionWrapper: { width: '100%', paddingHorizontal: 15, paddingBottom: 15 },
  rowContainer: { flexDirection: 'row', alignItems: 'center' },
  rowBorderBottom: { borderBottomColor: Colors.grey0, borderBottomWidth: 1 },
  tabItem: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  tabItemActive: {
    borderBottomColor: Colors.blue0,
  },
  tabText: {
    fontSize: 14,
  },
  tabTextActive: {
    color: Colors.blue0,
    fontFamily: 'figtree-medium',
  },
  tableRowMobile: {
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    paddingVertical: 15,
    paddingHorizontal: 10,
    width: '100%',
    flexDirection: 'row',
  },
  imageSummaryLog: {
    marginRight: 10,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: Colors.grey8,
    borderRadius: 4,
    padding: 1,
  },
});

ProductListingTab.propTypes = propTypes;

export default ProductListingTab;
